import { type FC, useState } from "react";
import {
  Toolbar,
  Checkbox,
  Button,
  Typography,
  Divider,
  Menu,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  ActionMenuItem,
  ToolbarProgress,
  useConfirmAction,
} from "@/shared/components";
import { useTranslation } from "react-i18next";
import {
  useUpdateContentsMutation,
  useDeleteContentsMutation,
  type ContentsItem,
} from "@/partner/entities/contents";
import { AddToCollectionButton } from "@/partner/features/collections";
import { AddToProductButton } from "@/partner/features/products";
import { useVideoProviderTextKeys } from "../model";

interface VideosToolbarProps {
  indeterminate: boolean;
  numSelected: number;
  nodes: ContentsItem[];
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedItems: {
    id: string;
    collectionIds: string[];
    productIds: string[];
  }[];
  onCloseSelected: () => void;
}

export const VideosToolbar: FC<VideosToolbarProps> = ({
  indeterminate,
  numSelected,
  nodes,
  onSelectAllClick,
  selectedItems,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const providerTextKeys = useVideoProviderTextKeys();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [updateContents, { loading: updateLoading }] =
    useUpdateContentsMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("videos.toolbar.updateSuccess"), {
          variant: "success",
        });
      },
      onError: (error: { message: string }) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["ContentsList"],
    });

  const [deleteContents, { loading: deleteLoading }] =
    useDeleteContentsMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("videos.toolbar.deleteSuccess"), {
          variant: "success",
        });
      },
      onError: (error: { message: string }) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["ContentsList"],
    });

  const onPublishSelected = () => {
    updateContents({
      variables: {
        ids: selectedItems.map((item) => item.id),
        input: {
          publishAt: new Date().toISOString(),
        },
      },
    });
  };

  const onUnpublishSelected = () => {
    updateContents({
      variables: {
        ids: selectedItems.map((item) => item.id),
        input: {
          publishAt: null,
        },
      },
    });
  };

  const onDeleteSelected = () => {
    confirm({
      title: t("common.actions.delete"),
      text: t(providerTextKeys.toolbarDeleteConfirm),
      // Промис отдаём наружу: диалог держится открытым с полосой прогресса,
      // пока идёт запрос, и закрывается сам по успеху.
      onConfirm: () =>
        deleteContents({
          variables: {
            ids: selectedItems.map((item) => item.id),
          },
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={numSelected === nodes.length}
        onChange={onSelectAllClick}
        sx={{
          marginLeft: "-16px",
          marginRight: {
            xs: 0,
            sm: "16px",
          },
        }}
      />

      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>

      <Divider
        orientation="vertical"
        variant="middle"
        sx={{
          mx: 1.25,
          height: "2em",
        }}
      />

      <Button
        color="inherit"
        onClick={handleClick}
        disabled={numSelected === 0}
      >
        {t("common.actions.actions")}
      </Button>

      <Menu
        id="contents-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onPublishSelected();
            handleClose();
          }}
        >
          {t("common.actions.publish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onUnpublishSelected();
            handleClose();
          }}
        >
          {t("common.actions.unpublish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={deleteLoading}
          onClick={() => {
            onDeleteSelected();
            handleClose();
          }}
        >
          {t("common.actions.delete")}
        </ActionMenuItem>

        <AddToCollectionButton
          menuItem
          disabled={numSelected === 0}
          selectedItems={selectedItems}
          refetchQueries={["ContentsList"]}
          onCompleted={() => {
            onCloseSelected();
            handleClose();
          }}
        />

        <AddToProductButton
          menuItem
          disabled={numSelected === 0}
          selectedItems={selectedItems}
          refetchQueries={["ContentsList"]}
          onCompleted={() => {
            onCloseSelected();
            handleClose();
          }}
        />
      </Menu>

      <ToolbarProgress loading={updateLoading || deleteLoading} />
      {confirmDialog}
    </Toolbar>
  );
};
